import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaUsers, FaCalendarAlt, FaChartLine, FaExclamationTriangle,
  FaCheckCircle, FaArrowRight, FaUserTie, FaClock
} from 'react-icons/fa';
import { useUser } from '../../../context/UserContext';
import styles from './ProgramHealthCards.module.css';

const ProgramHealthCards = ({ clubId, programs, loading }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [programsHealth, setProgramsHealth] = useState([]);
  const [healthLoading, setHealthLoading] = useState(true);

  useEffect(() => {
    const fetchProgramHealth = async () => {
      if (!programs || programs.length === 0 || !user) {
        setHealthLoading(false);
        return;
      }

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        
        // Get auth token
        const token = await user.getIdToken();
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };
        
        const healthData = await Promise.all(
          programs.map(async (program) => {
            try {
              // Fetch events for this program
              let upcomingEvents = 0;
              let eventsThisWeek = 0;
              try {
                const eventsRes = await fetch(`${backendUrl}/api/programs/${program._id}/events`, { headers });
                if (eventsRes.ok) {
                  const eventsData = await eventsRes.json();
                  const events = eventsData.events || eventsData || [];
                  const now = new Date();
                  const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
                  
                  upcomingEvents = events.filter(e => new Date(e.startTime || e.date) > now).length;
                  eventsThisWeek = events.filter(e => {
                    const eventDate = new Date(e.startTime || e.date);
                    return eventDate > now && eventDate <= weekFromNow;
                  }).length;
                }
              } catch (e) { /* ignore */ }

              // Fetch pending applications
              let pendingApps = 0;
              try {
                const appsRes = await fetch(`${backendUrl}/api/programs/${program._id}/applications/pending`, { headers });
                if (appsRes.ok) {
                  const appsData = await appsRes.json();
                  pendingApps = Array.isArray(appsData) ? appsData.length : 0;
                }
              } catch (e) { /* ignore */ }

              // Fetch assignments for overdue count
              let overdueEquipment = 0;
              try {
                const assignRes = await fetch(`${backendUrl}/api/equipment-assignments/program/${program._id}`, { headers });
                if (assignRes.ok) {
                  const assignData = await assignRes.json();
                  const assignments = Array.isArray(assignData) ? assignData : (assignData.assignments || []);
                  const now = new Date();
                  overdueEquipment = assignments.filter(a => {
                    const status = (a.status || '').toLowerCase();
                    if (status !== 'active' && status !== 'assigned') return false;
                    return a.dueDate && new Date(a.dueDate) < now;
                  }).length;
                }
              } catch (e) { /* ignore */ }

              // Calculate enrollment
              const currentEnrolled = program.capacity?.currentEnrolled || program.enrolledCount || 0;
              const maxCapacity = program.capacity?.maxParticipants || program.maxCapacity || 0;
              const enrollmentPercent = maxCapacity > 0 ? Math.round((currentEnrolled / maxCapacity) * 100) : 0;

              // Fetch program members to find director (director is stored as a member with role 'ProgramDirector')
              let hasDirector = false;
              let directorName = null;
              try {
                const membersRes = await fetch(`${backendUrl}/api/programs/${program._id}/members`, { headers });
                if (membersRes.ok) {
                  const membersData = await membersRes.json();
                  const membersList = Array.isArray(membersData) ? membersData : membersData.members || [];
                  
                  // Find director by role
                  const director = membersList.find(m => 
                    m.role === 'ProgramDirector' || m.role === 'programDirector'
                  );
                  
                  if (director) {
                    hasDirector = true;
                    const directorUser = director.user || director;
                    directorName = directorUser.firstName 
                      ? `${directorUser.firstName} ${directorUser.lastName || ''}`.trim()
                      : directorUser.name || directorUser.email || 'Director';
                  }
                }
              } catch (e) { /* ignore */ }
              
              // Fallback to program.director or program.programDirector if populated
              if (!hasDirector && (program.director || program.programDirector)) {
                const directorObj = program.director || program.programDirector;
                if (typeof directorObj === 'object' && directorObj.firstName) {
                  hasDirector = true;
                  directorName = `${directorObj.firstName} ${directorObj.lastName || ''}`.trim();
                }
              }

              // Calculate health status
              const alerts = [];
              if (!hasDirector) alerts.push({ type: 'critical', message: 'No director assigned' });
              if (pendingApps > 0) alerts.push({ type: 'high', message: `${pendingApps} pending registration${pendingApps > 1 ? 's' : ''}` });
              if (overdueEquipment > 0) alerts.push({ type: 'high', message: `${overdueEquipment} equipment overdue` });
              if (maxCapacity > 0 && enrollmentPercent < 25) alerts.push({ type: 'medium', message: 'Low enrollment' });
              if (upcomingEvents === 0) alerts.push({ type: 'medium', message: 'No upcoming events' });

              const healthStatus = alerts.some(a => a.type === 'critical') ? 'critical'
                : alerts.some(a => a.type === 'high') ? 'warning'
                : alerts.some(a => a.type === 'medium') ? 'attention'
                : 'healthy';

              return {
                ...program,
                health: {
                  status: healthStatus,
                  enrollment: { current: currentEnrolled, max: maxCapacity, percent: enrollmentPercent },
                  events: { upcoming: upcomingEvents, thisWeek: eventsThisWeek },
                  pendingApps,
                  overdueEquipment,
                  hasDirector,
                  directorName,
                  alerts
                }
              };
            } catch (e) {
              console.error('Error fetching health for', program.name, e);
              return {
                ...program,
                health: {
                  status: 'unknown',
                  enrollment: { current: 0, max: 0, percent: 0 },
                  events: { upcoming: 0, thisWeek: 0 },
                  pendingApps: 0,
                  overdueEquipment: 0,
                  hasDirector: false,
                  directorName: null,
                  alerts: []
                }
              };
            }
          })
        );

        // Sort by health status (critical first, then warning, etc.)
        const statusOrder = { critical: 0, warning: 1, attention: 2, healthy: 3, unknown: 4 };
        healthData.sort((a, b) => statusOrder[a.health.status] - statusOrder[b.health.status]);

        setProgramsHealth(healthData);
      } catch (error) {
        console.error('Error fetching program health:', error);
      } finally {
        setHealthLoading(false);
      }
    };

    fetchProgramHealth();
  }, [programs, user]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <span className={styles.statusDot + ' ' + styles.healthy}>●</span>;
      case 'attention': return <span className={styles.statusDot + ' ' + styles.attention}>●</span>;
      case 'warning': return <span className={styles.statusDot + ' ' + styles.warning}>●</span>;
      case 'critical': return <span className={styles.statusDot + ' ' + styles.critical}>●</span>;
      default: return <span className={styles.statusDot + ' ' + styles.unknown}>●</span>;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'healthy': return 'Healthy';
      case 'attention': return 'Needs Attention';
      case 'warning': return 'Warning';
      case 'critical': return 'Critical';
      default: return 'Unknown';
    }
  };

  const getProgramIcon = (program) => {
    const sport = (program.sport || program.category || '').toLowerCase();
    if (sport.includes('soccer') || sport.includes('football')) return '⚽';
    if (sport.includes('baseball')) return '⚾';
    if (sport.includes('basketball')) return '🏀';
    if (sport.includes('tennis')) return '🎾';
    if (sport.includes('hockey')) return '🏒';
    if (sport.includes('swim')) return '🏊';
    if (sport.includes('golf')) return '⛳';
    return '🏃';
  };

  const handleProgramClick = (programId) => {
    navigate(`/program/${programId}`);
  };

  const handleManageClick = (e, programId) => {
    e.stopPropagation();
    navigate(`/clubs/${clubId}/programs/${programId}/manage`);
  };

  const handleEventsClick = (e, programId) => {
    e.stopPropagation();
    navigate(`/clubs/${clubId}/programs/${programId}/events`);
  };

  const handleEnrollmentClick = (e, programId) => {
    e.stopPropagation();
    navigate(`/clubs/${clubId}/programs/${programId}/manage`);
  };

  if (loading || healthLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <FaChartLine className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>Program Health</h3>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
        </div>
      </div>
    );
  }

  if (!programsHealth || programsHealth.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <FaChartLine className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>Program Health</h3>
        </div>
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}>📋</span>
          <p>No programs found</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <FaChartLine className={styles.headerIcon} />
        <h3 className={styles.headerTitle}>Program Health</h3>
        <span className={styles.programCount}>{programsHealth.length} programs</span>
      </div>

      <div className={styles.cardsGrid}>
        {programsHealth.map((program) => (
          <div
            key={program._id}
            className={`${styles.programCard} ${styles[program.health.status]}`}
            onClick={() => handleProgramClick(program._id)}
          >
            {/* Card Header */}
            <div className={styles.cardHeader}>
              <span className={styles.programIcon}>{getProgramIcon(program)}</span>
              <div className={styles.programInfo}>
                <h4 className={styles.programName}>{program.name}</h4>
                <div className={styles.statusBadge}>
                  {getStatusIcon(program.health.status)}
                  <span>{getStatusLabel(program.health.status)}</span>
                </div>
              </div>
            </div>

            {/* Metrics Row */}
            <div className={styles.metricsRow}>
              {/* Enrollment - clickable */}
              <div 
                className={`${styles.metric} ${styles.clickable}`}
                onClick={(e) => handleEnrollmentClick(e, program._id)}
                title="View enrollments"
              >
                <FaUsers className={styles.metricIcon} />
                <div className={styles.metricData}>
                  <span className={styles.metricValue}>
                    {program.health.enrollment.current}
                    {program.health.enrollment.max > 0 && `/${program.health.enrollment.max}`}
                  </span>
                  <span className={styles.metricLabel}>enrolled</span>
                </div>
                {program.health.enrollment.max > 0 && (
                  <div className={styles.enrollmentBar}>
                    <div 
                      className={styles.enrollmentFill}
                      style={{ width: `${Math.min(program.health.enrollment.percent, 100)}%` }}
                    ></div>
                  </div>
                )}
              </div>

              {/* Events - clickable */}
              <div 
                className={`${styles.metric} ${styles.clickable}`}
                onClick={(e) => handleEventsClick(e, program._id)}
                title="View events"
              >
                <FaCalendarAlt className={styles.metricIcon} />
                <div className={styles.metricData}>
                  <span className={styles.metricValue}>{program.health.events.upcoming}</span>
                  <span className={styles.metricLabel}>upcoming</span>
                </div>
                {program.health.events.thisWeek > 0 && (
                  <span className={styles.thisWeekBadge}>{program.health.events.thisWeek} this week</span>
                )}
              </div>
            </div>

            {/* Director */}
            <div className={styles.directorRow}>
              <FaUserTie className={styles.directorIcon} />
              {program.health.hasDirector ? (
                <span className={styles.directorName}>{program.health.directorName}</span>
              ) : (
                <span className={styles.noDirector}>No director assigned</span>
              )}
            </div>

            {/* Alerts */}
            {program.health.alerts.length > 0 && (
              <div className={styles.alertsSection}>
                {program.health.alerts.map((alert, index) => (
                  <div key={index} className={`${styles.alert} ${styles[alert.type]}`}>
                    <FaExclamationTriangle className={styles.alertIcon} />
                    <span>{alert.message}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Footer */}
            <div className={styles.cardFooter}>
              <button 
                className={styles.manageBtn}
                onClick={(e) => handleManageClick(e, program._id)}
              >
                Manage <FaArrowRight />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProgramHealthCards;

